import { Dialog, Transition } from '@headlessui/react';
import {
  AlertTriangle,
  Briefcase,
  Building2,
  DoorOpen,
  Clock,
  CreditCard,
  DollarSign,
  FileText,
  Home,
  Home as HomeIcon,
  LogOut,
  Receipt,
  Settings,
  Shield,
  User,
  Users,
  Wrench,
  MessageSquare,
  X
} from 'lucide-react';
import React, { Fragment } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const MobileMenu = ({ open, setOpen }) => {
  const { user, logout } = useAuth();

  const isRmcUser =
    user?.role === 'rental_management_company' ||
    user?.role === 'rmc_admin' ||
    user?.role === 'rmc_staff' ||
    (user?.user_type === 'rmc_user' && user?.role === 'admin');

  // 사용자 역할에 따른 네비게이션 메뉴 (Sidebar와 동일한 로직)
  const getNavigation = () => {
    if (user?.role === 'super_admin') {
      return [
        { name: '총괄관리자 대시보드', href: '/super-admin', icon: Shield },
        { name: 'SoftFlo RMC 승인대기', href: '/super-admin/softflo-rmc-applications', icon: Briefcase },
        { name: 'SMS 관리', href: '/super-admin/sms-management', icon: MessageSquare },
      ];
    }

    if (user?.role === 'admin') {
      return [
        { name: '대시보드', href: '/', icon: Home },
        { name: '임대사업자 관리', href: '/property-owners', icon: Briefcase },
        { name: '건물 관리', href: '/buildings', icon: Building2 },
        { name: '세대 관리', href: '/buildings/1/units', icon: HomeIcon },
        { name: '임차인 관리', href: '/tenants', icon: Users },
        { name: '계약 관리', href: '/contracts', icon: FileText },
        { name: '공실관리', href: '/vacancy', icon: AlertTriangle },
        { name: '임대료 청구관리', href: '/billing', icon: DollarSign },
        { name: '입금관리', href: '/payment-management', icon: Receipt },
        { name: '연체관리', href: '/overdue', icon: Clock },
        { name: '유지보수', href: '/maintenance', icon: Wrench },
        { name: '공통모듈관리', href: '/common-modules', icon: Settings },
      ];
    }

    if (isRmcUser) {
      return [
        { name: '통합 대시보드', href: '/rmc-dashboard', icon: Shield },
        { name: '임대사업자 관리', href: '/property-owners', icon: Briefcase },
        { name: '건물 관리', href: '/rmc/buildings', icon: Building2 },
        { name: '세대 관리', href: '/rmc/units', icon: HomeIcon },
        { name: '임차인 관리', href: '/rmc/tenants', icon: Users },
        { name: '계약 관리', href: '/rmc/contracts', icon: FileText },
        { name: '공실관리', href: '/rmc/vacancy', icon: AlertTriangle },
        { name: '임대료 청구관리', href: '/rmc/billing', icon: DollarSign },
        { name: '입금관리', href: '/rmc/payment-management', icon: Receipt },
        { name: '연체관리', href: '/rmc/overdue', icon: Clock },
        { name: '입실/퇴실관리', href: '/rmc/move-in-out', icon: DoorOpen },
        { name: '월간 보고서', href: '/rmc/monthly-reports', icon: FileText },
        { name: '운영비관리', href: '/rmc/operating-expenses', icon: Receipt },
        { name: '유지보수', href: '/rmc/maintenance', icon: Wrench },
        { name: '공통모듈관리', href: '/common-modules', icon: Settings },
      ];
    }

    if (user?.role === 'property_owner') {
      return [
        { name: '대시보드', href: '/', icon: Home },
        { name: '건물관리', href: '/buildings', icon: Building2 },
        { name: '세대관리', href: '/units', icon: HomeIcon },
        { name: '내 계약', href: '/contracts', icon: FileText },
        { name: '내 임차인', href: '/tenants', icon: Users },
        { name: '공실관리', href: '/vacancy', icon: AlertTriangle },
        { name: '임대료 청구관리', href: '/billing', icon: DollarSign },
        { name: '입금관리', href: '/payment-management', icon: Receipt },
        { name: '연체관리', href: '/overdue', icon: Clock },
        { name: '유지보수', href: '/maintenance', icon: Wrench },
        { name: '수입비용관리', href: '/income-expense', icon: CreditCard },
        { name: '공통모듈관리', href: '/common-modules', icon: Settings },
      ];
    }

    // 기본 메뉴 (기타 역할)
    return [
      { name: '대시보드', href: '/', icon: Home },
      { name: '건물 관리', href: '/buildings', icon: Building2 },
      { name: '세대 관리', href: '/buildings/1/units', icon: HomeIcon },
      { name: '임차인 관리', href: '/tenants', icon: Users },
      { name: '계약 관리', href: '/contracts', icon: FileText },
      { name: '공실관리', href: '/vacancy', icon: AlertTriangle },
      { name: '임대료 청구관리', href: '/billing', icon: DollarSign },
      { name: '입금관리', href: '/payment-management', icon: Receipt },
      { name: '연체관리', href: '/overdue', icon: Clock },
      { name: '유지보수', href: '/maintenance', icon: Wrench },
      { name: '공통모듈관리', href: '/common-modules', icon: Settings },
    ];
  };

  const navigation = getNavigation();

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50 lg:hidden" onClose={setOpen}>
        <Transition.Child
          as={Fragment}
          enter="transition-opacity ease-linear duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity ease-linear duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" />
        </Transition.Child>

        <div className="fixed inset-0 z-40 flex">
          <Transition.Child
            as={Fragment}
            enter="transition ease-in-out duration-300 transform"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
          >
            <Dialog.Panel className="relative flex h-full w-full max-w-xs flex-1 flex-col bg-white">
              <Transition.Child
                as={Fragment}
                enter="ease-in-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in-out duration-300"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="absolute top-0 right-0 -mr-12 pt-2">
                  <button
                    type="button"
                    className="ml-1 flex h-10 w-10 items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                    onClick={() => setOpen(false)}
                  >
                    <X className="h-6 w-6 text-white" />
                  </button>
                </div>
              </Transition.Child>

              {/* 로고 */}
              <div className="flex items-center h-16 px-6 border-b border-gray-200">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-white" />
                  </div>
                  <span className="ml-3 text-xl font-semibold text-gray-900">
                    RentalHub
                  </span>
                </div>
              </div>

              {/* 네비게이션 */}
              <nav className="flex-1 min-h-0 overflow-y-auto px-4 py-6 space-y-2">
                {navigation.map((item) => {
                  const Icon = item.icon;
                  return (
                    <NavLink
                      key={item.name}
                      to={item.href}
                      onClick={() => setOpen(false)}
                      className={({ isActive }) =>
                        `group flex items-center px-3 py-2 text-base font-medium rounded-md transition-colors ${
                          isActive
                            ? 'bg-primary-50 text-primary-700'
                            : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                        }`
                      }
                    >
                      <Icon className="mr-4 h-6 w-6" />
                      {item.name}
                    </NavLink>
                  );
                })}
              </nav>

              {/* 사용자 정보 및 로그아웃 */}
              <div className="shrink-0 border-t border-gray-200 bg-white p-4">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-primary-600" />
                  </div>
                  <div className="ml-3">
                    <p className="text-base font-medium text-gray-900">
                      {user?.name || '사용자'}
                    </p>
                    <p className="text-sm text-gray-500">
                      {user?.role === 'admin' ? '관리자' : 
                       user?.role === 'property_owner' ? '임대인' : '임대관리자'}
                    </p>
                  </div>
                </div>
                
                <NavLink
                  to="/profile"
                  onClick={() => setOpen(false)}
                  className="group flex items-center px-3 py-2 text-base font-medium text-gray-700 rounded-md hover:bg-gray-50 hover:text-gray-900 transition-colors"
                >
                  <User className="mr-4 h-6 w-6" />
                  프로필
                </NavLink>
                
                <button
                  onClick={() => {
                    logout();
                    setOpen(false);
                  }}
                  className="group flex items-center w-full px-3 py-2 text-base font-medium text-gray-700 rounded-md hover:bg-gray-50 hover:text-gray-900 transition-colors"
                >
                  <LogOut className="mr-4 h-6 w-6" />
                  로그아웃
                </button>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default MobileMenu;
